/**
 * 지문(SECOM) ±5분 대조 — app.ts에서 추출.
 * eventDatetime은 Seoul 로컬 wall-clock("YYYYMMDDHHmmss"), 출퇴근 시각은 UTC 저장.
 * 둘 다 org-로컬 분 단위 정수로 변환해 차이를 비교한다. 포맷터는 주입(deps).
 */

export const SECOM_MATCH_MINUTES = 5;

export interface SecomDeps {
  /** UTC 값 → org-로컬 "YYYY-MM-DD" */
  formatDateISO(value: string): string;
  /** UTC 값 → org-로컬 "HH:MM" (24h) */
  formatTimeOnly(value: string): string;
}

export function localMinutes(target: unknown, deps: SecomDeps): number | null {
  if (!target) {
    return null;
  }
  const iso = deps.formatDateISO(target as string);
  const hm = deps.formatTimeOnly(target as string);
  if (!(iso && hm)) {
    return null;
  }
  const [y, mo, d] = iso.split("-").map(Number);
  const [h, mi] = hm.split(":").map(Number);
  return Math.floor(Date.UTC(y, mo - 1, d, h, mi) / 60000);
}

export function secomMinutes(dt: string): number | null {
  if (dt.length < 12) {
    return null;
  }
  const y = Number(dt.slice(0, 4));
  const mo = Number(dt.slice(4, 6));
  const d = Number(dt.slice(6, 8));
  const h = Number(dt.slice(8, 10));
  const mi = Number(dt.slice(10, 12));
  return Math.floor(Date.UTC(y, mo - 1, d, h, mi) / 60000);
}

/** ±허용오차 내 가장 가까운 지문 1건 — nextDay prefix 허용 여부는 호출부 규칙. */
function nearestEvent(
  events: Record<string, unknown>[],
  targetMin: number,
  compact: string,
  nextDay: string,
  allowNextDay: boolean,
  wantType: string,
): Record<string, unknown> | null {
  let best: Record<string, unknown> | null = null;
  let bestDiff = Number.POSITIVE_INFINITY;
  for (const ev of events) {
    const type = String(ev.eventType || "unknown");
    if (type !== wantType && type !== "unknown") {
      continue;
    }
    const dt = String(ev.eventDatetime || "");
    if (!(dt.startsWith(compact) || (allowNextDay && dt.startsWith(nextDay)))) {
      continue;
    }
    const em = secomMinutes(dt);
    if (em === null) {
      continue;
    }
    const diff = Math.abs(em - targetMin);
    if (diff <= SECOM_MATCH_MINUTES && diff < bestDiff) {
      best = ev;
      bestDiff = diff;
    }
  }
  return best;
}

function dayPrefixes(dateIso: string): { compact: string; nextDay: string } {
  const compact = dateIso.replace(/-/g, "");
  const nextDay = new Date(new Date(dateIso).getTime() + 86400000)
    .toISOString()
    .slice(0, 10)
    .replace(/-/g, "");
  return { compact, nextDay };
}

/** 해당 행의 출근/퇴근 시각과 ±5분 내 지문(가장 가까운 1건). 퇴근만 익일 prefix 허용. */
export function matchSecomEvent(
  r: Record<string, unknown>,
  which: "in" | "out",
  events: Record<string, unknown>[],
  deps: SecomDeps,
): Record<string, unknown> | null {
  const target =
    which === "in"
      ? (r.actualCheckIn as string) || (r.checkIn as string)
      : (r.actualCheckOut as string) || (r.checkOut as string);
  const tMin = localMinutes(target, deps);
  if (tMin === null) {
    return null;
  }
  const { compact, nextDay } = dayPrefixes(String(r.date || "").slice(0, 10));
  const wantType = which === "in" ? "check_in" : "check_out";
  return nearestEvent(events, tMin, compact, nextDay, which === "out", wantType);
}

/** 퇴근 미입력 + 소정 퇴근 ±5분 지문 — 추정 퇴근 후보. 익일 prefix 항상 허용. */
export function checkoutGuess(
  r: Record<string, unknown>,
  events: Record<string, unknown>[],
  deps: SecomDeps,
): Record<string, unknown> | null {
  if (r.actualCheckOut || r.checkOut) {
    return null; // 이미 퇴근 기록 있음
  }
  const sMin = localMinutes(r.scheduledCheckOut, deps);
  if (sMin === null) {
    return null;
  }
  const { compact, nextDay } = dayPrefixes(String(r.date || "").slice(0, 10));
  return nearestEvent(events, sMin, compact, nextDay, true, "check_out");
}
